import { escapeHtml } from '@core/utils/html-escape.utils';

/**
 * The template's only script: sorting for `table[data-sortable]`, and filter chips, search and a row count
 * for `table[data-filter-column]` (1-based). It finds its tables by attribute and keeps references to the
 * controls it creates; it never looks anything up by id, because ids the model wrote (SVG markers) survive
 * sanitizing and could shadow one. Its copy comes from the `data-label-*` attributes on `<html>`, which the
 * body cannot reach.
 */
export const REPORT_SCRIPT = String.raw`(() => {
  const root = document.documentElement;
  const label = (name) => root.getAttribute('data-label-' + name) || '';
  const cellText = (row, column) => (row.cells[column] ? row.cells[column].textContent.trim() : '');
  const commaDecimal = /^(fr|es|pt)(?:[-_]|$)/i.test(root.lang);
  const toNumber = (text) => {
    let s = text.replace(/\s/g, '').replace(/\u2212/g, '-').replace(/[\p{L}%]+$/u, '');
    if (!/^[+-]?\d[\d.,]*$/.test(s)) return null;
    const dot = s.lastIndexOf('.');
    const comma = s.lastIndexOf(',');
    if (dot >= 0 && comma >= 0) {
      const decimal = dot > comma ? '.' : ',';
      s = s.split(decimal === '.' ? ',' : '.').join('').replace(decimal, '.');
    } else if (comma >= 0) {
      const decimals = s.length - comma - 1;
      const decimal = s.indexOf(',') === comma && (decimals <= 2 || (decimals === 3 && commaDecimal));
      s = decimal ? s.replace(',', '.') : s.split(',').join('');
    } else if (dot >= 0) {
      const decimals = s.length - dot - 1;
      if (s.indexOf('.') !== dot || (decimals === 3 && commaDecimal)) s = s.split('.').join('');
    }
    const value = Number(s);
    return Number.isFinite(value) ? value : null;
  };
  const collator = new Intl.Collator(root.lang || undefined, { numeric: true, sensitivity: 'base' });

  for (const table of document.querySelectorAll('table[data-sortable]')) {
    const body = table.tBodies[0];
    const head = table.tHead && table.tHead.rows[0];
    if (!body || !head) continue;
    const heads = Array.from(head.cells);
    heads.forEach((th, column) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'report-sort';
      button.append(...th.childNodes);
      th.append(button);
      button.addEventListener('click', () => {
        const keys = Array.from(body.rows, (row, index) => {
          const text = cellText(row, column);
          return { row, index, text, value: toNumber(text) };
        });
        const numeric = keys.every((k) => k.text === '' || k.value !== null);
        const current = th.getAttribute('aria-sort');
        const descending = current ? current === 'ascending' : numeric;
        heads.forEach((other) => other.removeAttribute('aria-sort'));
        th.setAttribute('aria-sort', descending ? 'descending' : 'ascending');
        keys.sort((a, b) => {
          if (a.text === '' || b.text === '') {
            return Number(a.text === '') - Number(b.text === '') || a.index - b.index;
          }
          const order = numeric ? a.value - b.value : collator.compare(a.text, b.text);
          return (descending ? -order : order) || a.index - b.index;
        });
        for (const k of keys) body.append(k.row);
      });
    });
  }

  for (const table of document.querySelectorAll('table[data-filter-column]')) {
    const body = table.tBodies[0];
    if (!body) continue;
    const rows = Array.from(body.rows);
    const column = Number(table.getAttribute('data-filter-column')) - 1;
    const values = Number.isInteger(column) && column >= 0
      ? Array.from(new Set(rows.map((row) => cellText(row, column)).filter(Boolean)))
      : [];
    const bar = document.createElement('div');
    bar.className = 'report-filters';
    const search = document.createElement('input');
    const count = document.createElement('span');
    let chosen = null;
    const apply = () => {
      const term = search.value.trim().toLowerCase();
      let shown = 0;
      for (const row of rows) {
        const visible = (chosen === null || cellText(row, column) === chosen)
          && (!term || row.textContent.toLowerCase().includes(term));
        row.hidden = !visible;
        if (visible) shown++;
      }
      count.textContent = label('count').replace('{shown}', String(shown)).replace('{total}', String(rows.length));
    };
    if (values.length > 1 && values.length <= 12) {
      const lead = document.createElement('span');
      lead.className = 'report-filters-label';
      lead.textContent = label('show');
      const chips = [null, ...values].map((value) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'report-chip';
        chip.textContent = value === null ? label('all') : value;
        chip.setAttribute('aria-pressed', String(value === null));
        chip.addEventListener('click', () => {
          chosen = value;
          chips.forEach((other) => other.setAttribute('aria-pressed', String(other === chip)));
          apply();
        });
        return chip;
      });
      bar.append(lead, ...chips);
    }
    search.type = 'search';
    search.className = 'report-search';
    search.placeholder = label('search');
    search.setAttribute('aria-label', label('search'));
    search.addEventListener('input', apply);
    count.className = 'report-count';
    count.setAttribute('aria-live', 'polite');
    bar.append(search, count);
    const scroller = table.parentElement;
    (scroller && scroller.classList.contains('report-scroll') ? scroller : table).before(bar);
    apply();
  }
})();`;

/**
 * Base64 SHA-256 of REPORT_SCRIPT; a spec recomputes it from the emitted script.
 *
 * A srcdoc iframe also obeys the embedding page's CSP. Galia sends none today; if one is added, its
 * script-src must allow this hash, or previews lose sorting and filtering (charts are static SVG and keep
 * working).
 */
export const REPORT_SCRIPT_HASH = "'sha256-HB+gHgjaoVj9YkUwjSyEroxUiqOpuphrVnwcIQsqlkw='";

export const REPORT_CSP = `default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:; script-src ${REPORT_SCRIPT_HASH}; form-action 'none'; base-uri 'none'`;

export type ReportLegendRole = 'brand' | 'parent' | 'good' | 'bad' | 'review';

export interface ReportDocument {
  title: string;
  bodyHtml: string;
  generatedAt: string;
  footer: string;
  provenance: string;
  lang: string;
  summary: string | null;
  subject: string | null;
  meta: string[];
  legend: { role: ReportLegendRole; label: string }[];
  fontFaces: string;
  /** Copy for the controls REPORT_SCRIPT creates; `count` holds `{shown}` and `{total}`. */
  tableLabels: { show: string; all: string; search: string; count: string };
}

const LIGHT = `--paper:#f3f5f6;--surface:#fff;--ink:#17232a;--ink-2:#4a5960;--ink-3:#7b878e;--rule:#d3dadd;--brand:#1f5563;--brand-2:#3f8496;--parent:#a3adb2;--good:#24785a;--bad:#bf3a2b;--review:#a8650c;--tint-brand:rgba(31,85,99,.07);--tint-good:rgba(36,120,90,.08);--tint-bad:rgba(191,58,43,.07);--tint-review:rgba(168,101,12,.08);--on-ink:#f3f5f6`;
const DARK = `--paper:#0f1619;--surface:#162024;--ink:#e2e9eb;--ink-2:#a6b3b8;--ink-3:#74838a;--rule:#28353a;--brand:#7cc3d4;--brand-2:#4f9fb2;--parent:#5d6b71;--good:#5fc39a;--bad:#ff7d6b;--review:#eea446;--tint-brand:rgba(124,195,212,.1);--tint-good:rgba(95,195,154,.1);--tint-bad:rgba(255,125,107,.1);--tint-review:rgba(238,164,70,.1);--on-ink:#0f1619`;

// Green and red mean only better or worse than the comparison, amber only "to review", teal only this plan
// or Galia. The body's vocabulary carries roles (data-role), never colours. The dark palette is screen-only,
// so a report printed from a dark-mode browser still prints light.
const REPORT_CSS = `
:root{color-scheme:light;${LIGHT};--font-display:"Bricolage Grotesque","Avenir Next","Segoe UI",Arial,sans-serif;--font-body:"Source Serif 4","Iowan Old Style",Georgia,serif;--font-mono:"IBM Plex Mono",ui-monospace,"SFMono-Regular",Menlo,Consolas,monospace;--step--1:.8125rem;--step-0:1.0625rem;--step-1:1.3rem;--step-2:1.7rem;--step-3:2.3rem;--step-4:3.4rem}
@media screen and (prefers-color-scheme:dark){:root{color-scheme:dark;${DARK}}}
*{box-sizing:border-box}
html{-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{margin:0;padding:0 16px 4rem;background:var(--paper);color:var(--ink);font-family:var(--font-body);font-size:var(--step-0);line-height:1.6;overflow-wrap:break-word}
.page{max-width:1140px;margin:0 auto}
h1,h2,h3{margin:0;font-family:var(--font-display);line-height:1.12;text-wrap:balance}
h1{font-size:var(--step-4);font-weight:750;letter-spacing:-.02em}
h2{font-size:var(--step-3);font-weight:650;letter-spacing:-.01em}
h3{font-size:var(--step-1);font-weight:650}
p,ul,ol,blockquote,pre{margin:0}
p,ul,ol{max-width:66ch}
ul,ol{padding-left:1.3rem}
li+li{margin-top:.3rem}
li::marker{color:var(--ink-3)}
strong,b{font-weight:600}
a{color:var(--brand);text-underline-offset:2px}
hr{margin:0;border:0;border-top:1px solid var(--rule)}
blockquote{padding-left:1rem;border-left:3px solid var(--rule);color:var(--ink-2)}
code{font-family:var(--font-mono);font-size:.8em}
code.id{font-weight:500;color:var(--brand);white-space:nowrap}
pre{max-width:100%;padding:.75rem 1rem;overflow-x:auto;font:.8rem/1.5 var(--font-mono);background:var(--surface);border:1px solid var(--rule)}
pre code{font-size:inherit}
.masthead{display:grid;gap:1rem;padding-block:3.2rem 2rem;border-bottom:3px double var(--ink)}
.brandline{display:flex;flex-wrap:wrap;align-items:center;gap:.5rem;font-family:var(--font-display);font-size:1.05rem;font-weight:750;letter-spacing:-.01em;color:var(--brand)}
.brandline .mark{display:grid;gap:2px;width:16px}
.brandline .mark i{display:block;height:3px;background:var(--brand)}
.brandline .mark i:nth-child(2){width:72%;background:var(--brand-2)}
.brandline .mark i:nth-child(3){width:46%;background:var(--parent)}
.brandline .stamp{margin-left:auto;font-size:var(--step--1);font-weight:400;letter-spacing:0;color:var(--ink-3)}
.context,section[data-label]::before,.figure .label,.report-filters-label{font-family:var(--font-display);font-size:var(--step--1);font-weight:650;letter-spacing:.14em;text-transform:uppercase;color:var(--ink-2)}
.lede{max-width:62ch;font-size:var(--step-1);line-height:1.5;color:var(--ink-2)}
.legend,.meta-row{display:flex;flex-wrap:wrap;font-family:var(--font-display);font-size:var(--step--1)}
.legend{gap:.5rem 1.2rem}
.legend span{display:inline-flex;align-items:center;gap:.45rem}
.legend i{display:inline-block;width:.8rem;height:.8rem;border-radius:2px}
[data-swatch=brand]{background:var(--brand)}
[data-swatch=parent]{background:var(--parent)}
[data-swatch=good]{background:var(--good)}
[data-swatch=bad]{background:var(--bad)}
[data-swatch=review]{background:var(--review)}
.meta-row{gap:.4rem 1.5rem;color:var(--ink-3)}
.report-body{padding-top:1rem}
.report-body>*,section>*{margin-block:0}
.report-body>*+*,section>*+*{margin-top:1.25rem}
.report-body>section{margin-top:0;padding-top:3.2rem}
section[data-label]::before{content:attr(data-label);display:block;margin-bottom:.4rem;color:var(--brand)}
.figures{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,12rem),1fr));border-top:2px solid var(--ink);border-bottom:1px solid var(--rule)}
.figure{display:flex;flex-wrap:wrap;align-content:start;align-items:baseline;gap:.2rem .35rem;padding:.9rem 1.1rem 1.1rem 0}
.figure+.figure{padding-left:1.1rem;border-left:1px solid var(--rule)}
.figure .label{flex-basis:100%;font-size:.72rem;letter-spacing:.12em}
.figure .value{font-family:var(--font-display);font-size:var(--step-3);font-weight:750;line-height:1.1;letter-spacing:-.02em;font-variant-numeric:tabular-nums}
.figure .unit{font-family:var(--font-display);font-size:1rem;font-weight:500;color:var(--ink-2)}
.figure .delta{flex-basis:100%;font-family:var(--font-mono);font-size:.78rem;color:var(--ink-3)}
.delta[data-role=better]{color:var(--good)}
.delta[data-role=worse]{color:var(--bad)}
.tag{padding:.05rem .4rem;font-family:var(--font-display);font-size:.68rem;font-weight:650;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap;color:var(--ink-2);border:1px solid currentColor;border-radius:3px}
.tag[data-role=better]{color:var(--good)}
.tag[data-role=worse]{color:var(--bad)}
.tag[data-role=review]{color:var(--review)}
.callout{max-width:78ch;padding:.55rem .8rem;font-family:var(--font-display);font-size:var(--step--1);line-height:1.45;background:var(--tint-brand);border-left:3px solid var(--brand)}
.callout>*+*{margin-top:.5rem}
.callout[data-role=better]{background:var(--tint-good);border-left-color:var(--good)}
.callout[data-role=worse]{background:var(--tint-bad);border-left-color:var(--bad)}
.callout[data-role=review]{background:var(--tint-review);border-left-color:var(--review)}
figure{margin:0}
figure>*+*{margin-top:.6rem}
figcaption{max-width:78ch;font-family:var(--font-display);font-size:var(--step--1);line-height:1.5;color:var(--ink-2)}
.report-body svg{display:block;max-width:min(100%,42rem);height:auto}
svg text{font-family:var(--font-display)}
figure[data-chart]>svg{padding:1rem 1.2rem;background:var(--surface);border:1px solid var(--rule)}
.chart-data{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip-path:inset(50%);white-space:nowrap;border:0}
.chart-fallback{font-family:var(--font-display);font-size:var(--step--1);color:var(--ink-3)}
.report-scroll{max-width:100%;overflow-x:auto}
table{width:100%;border-collapse:collapse;font-size:.9rem}
caption{padding-bottom:.5rem;text-align:left;font-family:var(--font-display);font-size:var(--step--1);color:var(--ink-2)}
th{padding:.5rem .75rem .5rem 0;text-align:left;font-family:var(--font-display);font-size:.72rem;font-weight:650;letter-spacing:.1em;text-transform:uppercase;white-space:nowrap;color:var(--ink-2);border-bottom:2px solid var(--ink)}
td{padding:.55rem .75rem .55rem 0;vertical-align:top;border-bottom:1px solid var(--rule)}
th:last-child,td:last-child{padding-right:0}
th.num,td.num{text-align:right;font-variant-numeric:tabular-nums}
td.num{font-family:var(--font-mono);font-size:.8rem;white-space:nowrap}
.report-sort{all:unset;cursor:pointer}
.report-sort:hover{color:var(--ink)}
.report-sort:focus-visible,.report-chip:focus-visible,.report-search:focus-visible,summary:focus-visible{outline:2px solid var(--brand);outline-offset:2px}
th[aria-sort=ascending] .report-sort::after{content:" ↑"}
th[aria-sort=descending] .report-sort::after{content:" ↓"}
.report-filters{display:flex;flex-wrap:wrap;align-items:center;gap:.4rem}
.report-filters+.report-scroll{margin-top:.6rem}
.report-filters-label{margin-right:.25rem;font-size:.72rem;letter-spacing:.12em;color:var(--ink-3)}
.report-chip{padding:.2rem .7rem;font:500 var(--step--1) var(--font-display);color:var(--ink);background:var(--surface);border:1px solid var(--rule);border-radius:999px;cursor:pointer}
.report-chip:hover{border-color:var(--ink-2)}
.report-chip[aria-pressed=true]{color:var(--on-ink);background:var(--ink);border-color:var(--ink)}
.report-search{width:13rem;max-width:100%;padding:.3rem .7rem;font:400 var(--step--1) var(--font-display);color:var(--ink);background:var(--surface);border:1px solid var(--rule);border-radius:999px}
.report-count{font-family:var(--font-display);font-size:var(--step--1);font-variant-numeric:tabular-nums;color:var(--ink-3)}
details>*+*{margin-top:.75rem}
summary{cursor:pointer;font-family:var(--font-display);font-size:var(--step--1);color:var(--brand)}
.report-foot{display:flex;flex-wrap:wrap;justify-content:space-between;gap:.5rem;margin-top:4rem;padding-top:1rem;font-family:var(--font-display);font-size:var(--step--1);color:var(--ink-3);border-top:1px solid var(--rule)}
@media (max-width:640px){
.masthead{padding-block:2rem 1.5rem}
h1{font-size:2.4rem}
h2{font-size:1.75rem}
.figures{grid-template-columns:repeat(2,minmax(0,1fr))}
.figure+.figure{padding-left:0;border-left:0}
.figure:nth-child(even){padding-left:.9rem;border-left:1px solid var(--rule)}
.figure:nth-child(n+3){border-top:1px solid var(--rule)}
.figure .value{font-size:var(--step-2)}
figure[data-chart]>svg{padding:.5rem}
}
@media print{
body{padding:0}
.report-filters{display:none}
.report-scroll{overflow:visible}
.report-sort::after{content:none!important}
thead{display:table-header-group}
figure,tr,.figure,.callout{break-inside:avoid}
h2,h3,section[data-label]::before{break-after:avoid}
}
`;

const masthead = (doc: ReportDocument): string => {
  const generated = escapeHtml(doc.generatedAt);
  const parts = [
    `<div class="brandline"><span class="mark" aria-hidden="true"><i></i><i></i><i></i></span>Galia${
      doc.meta.length ? '' : `<span class="stamp">${generated}</span>`
    }</div>`
  ];
  if (doc.subject) parts.push(`<div class="context">${escapeHtml(doc.subject)}</div>`);
  parts.push(`<h1>${escapeHtml(doc.title)}</h1>`);
  if (doc.summary) parts.push(`<p class="lede">${escapeHtml(doc.summary)}</p>`);
  if (doc.legend.length) {
    const items = doc.legend.map(
      (item) => `<span><i data-swatch="${item.role}"></i>${escapeHtml(item.label)}</span>`
    );
    parts.push(`<div class="legend">${items.join('')}</div>`);
  }
  if (doc.meta.length) {
    const items = [doc.generatedAt, ...doc.meta].map(
      (m) => `<span>${escapeHtml(m)}</span>`
    );
    parts.push(`<div class="meta-row">${items.join('')}</div>`);
  }
  return `<header class="masthead">${parts.join('\n')}</header>`;
};

export function buildReportHtml(doc: ReportDocument): string {
  const labels = Object.entries(doc.tableLabels)
    .map(([name, text]) => ` data-label-${name}="${escapeHtml(text)}"`)
    .join('');
  // The CSP comes first: a <meta> policy only governs what the document parses after it.
  return `<!doctype html>
<html lang="${escapeHtml(doc.lang)}"${labels}>
<head>
<meta http-equiv="Content-Security-Policy" content="${REPORT_CSP}">
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(doc.title)}</title>
<style>${doc.fontFaces}${REPORT_CSS}</style>
</head>
<body>
<div class="page">
${masthead(doc)}
<main class="report-body">
${doc.bodyHtml}
</main>
<footer class="report-foot"><span>${escapeHtml(doc.footer)}</span><span>${escapeHtml(doc.provenance)}</span></footer>
</div>
<script>${REPORT_SCRIPT}</script>
</body>
</html>
`;
}

export function formatFileSize(content: string): string {
  const bytes = new TextEncoder().encode(content).length;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
